import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", role: "customer",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to MaaPrint, ${u.name.split(" ")[0]}!`);
      nav("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-brand-green via-brand-blue to-brand-blue overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <Logo size={40} className="[&_span]:!text-white" />
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70 mb-3">Join today</p>
            <h2 className="text-4xl font-display font-bold leading-tight max-w-md">
              Start printing in under 60 seconds.
            </h2>
            <p className="mt-4 text-white/80 max-w-md">
              Free to sign up. Free wallet. Only pay per print.
            </p>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} MaaPrint</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo size={40} />
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Already have one? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">Sign in</Link>
          </p>

          <Card className="mt-8 p-6 border-border bg-card">
            <form onSubmit={submit} className="space-y-4">
              {err && (
                <Alert variant="destructive" data-testid="register-error">
                  <AlertDescription>{err}</AlertDescription>
                </Alert>
              )}
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={upd("email")} className="mt-1.5" data-testid="register-email-input" />
              </div>
              <div>
                <Label htmlFor="phone">Mobile (optional)</Label>
                <Input id="phone" value={form.phone} onChange={upd("phone")} className="mt-1.5" placeholder="+91" data-testid="register-phone-input" />
              </div>
              <div>
                <Label htmlFor="role">I am a…</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="register-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="retailer">Retailer / CSC Center</SelectItem>
                    <SelectItem value="distributor">Distributor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="password">Password (min 6 chars)</Label>
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="mt-1.5" data-testid="register-password-input" />
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-brand-blue-hover h-11"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <UserPlus size={16} className="mr-2" />}
                Create account
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
